




































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { membershipState } from '../../store/modules/MembershipState'
import NonLoginAlertModal from '../../components/Membership/Login/NonLoginAlertModal.vue'
import ErrorFormResponse from '../../modules/class/ErrorFormResponse'
import JsonUtil from '../../modules/util/JsonUtil'
import PathUtil from '../../modules/util/PathUtil'
import { executeApiAfterLoginCheck } from '../../modules/util/SapUtil'
import { createCustomEvent } from '@/modules/CustomEvent'

type ApiResponse = {
  result: string
  errordata: Record<string, unknown>
}

@Component({
  components: { NonLoginAlertModal }
})
export default class BookmarkInPage extends Vue {
  // ------------------------------------------------------------------------------------------------
  // prop

  // Bookmarkモバイルから呼び出されているかの有無
  @Prop({ default: false }) calledBookmarkMobile!: boolean

  // ------------------------------------------------------------------------------------------------
  // data
  private isNonLoginAlertModalVisible = false
  private resultError: ErrorFormResponse = {}

  // 変更中フラグ
  private changing = false

  // ------------------------------------------------------------------------------------------------
  // hooks
  private created() {
    if (!this.calledBookmarkMobile) {
      // Bookmarkチェックイベント追加
      document.addEventListener(
        'bookmark-checks',
        async (
          event: CustomEventInit<{
            urlPathList: string[]
            callback: (result: Record<string, boolean>) => void
          }>
        ) => {
          if (event.detail !== undefined) {
            const result = await this.checkBookmarks(event.detail.urlPathList)
            event.detail.callback(result)
          }
        }
      )
      // Bookmark追加イベント追加
      document.addEventListener(
        'bookmark-add',
        async (
          event: CustomEventInit<{
            urlPath: string
            title: string
            callback: (result: boolean) => void
          }>
        ) => {
          if (event.detail !== undefined) {
            const result = await this.addBookmark(
              event.detail.urlPath,
              event.detail.title
            )
            event.detail.callback(result)
          }
        }
      )
      // Bookmark削除イベント追加
      document.addEventListener(
        'bookmark-remove',
        async (
          event: CustomEventInit<{
            urlPath: string
            callback: (result: boolean) => void
          }>
        ) => {
          if (event.detail !== undefined) {
            const result = await this.removeBookmark(event.detail.urlPath)
            event.detail.callback(result)
          }
        }
      )

      // Bookmark更新イベント追加
      document.addEventListener(
        'bookmark-update',
        async (
          event: CustomEventInit<{ result: boolean; urlPath: string }>
        ) => {
          if (event.detail !== undefined) {
            if (event.detail.urlPath === window.location.pathname) {
              this.fetchCurrentPageBookmark()
            }
          }
        }
      )
    }
  }

  // ------------------------------------------------------------------------------------------------
  // computed
  get isLogin(): boolean {
    return membershipState.isLogin
  }

  /**
   * ブックマークアイコンを表示するか
   */
  get showBookmarkIcon(): boolean {
    // ローディング判定中は非表示
    return !membershipState.isLoadingLogin
  }

  // -----------------------------------------------------------------------------
  // watcher
  @Watch('isLogin')
  onIsLoginChanged(newIsLogin: boolean): void {
    if (newIsLogin) {
      // ログイン状態になったら、
      // 現在のページのログイン状態を取得する
      this.fetchCurrentPageBookmark()
    } else {
      // ログアウト状態になったら、
      // すべてのアイコンをOFFにする
      createCustomEvent('bookmark-update', { result: false, urlPath: null })
    }
  }

  // ------------------------------------------------------------------------------------------------
  // 現在のページの状態を判定

  /**
   * 現在のページのブックマーク状態
   */
  private get isBookmarked() {
    return membershipState.isBookmarked
  }

  /**
   * 現在のページの最新のブックマーク状態を取得する
   */
  public async fetchCurrentPageBookmark() {
    this.changing = true
    const result = await this.checkBookmark(location.pathname)
    membershipState.addIsBookmarked(result)
    this.changing = false
  }

  /**
   * 現在のページをブックマーク追加
   */
  public async addCurrentPageBookmark() {
    this.changing = true

    const result = await this.addBookmark(
      location.pathname,
      document.getElementsByTagName('h1')[0].innerText ?? ''
    )

    // Bookmarkのステータスを変更
    membershipState.addIsBookmarked(result)

    this.changing = false
  }

  /**
   * 現在のページをブックマーク削除
   */
  public async removeCurrentPageBookmark() {
    this.changing = true

    const result = await this.removeBookmark(location.pathname)

    // Bookmarkのステータスを変更
    if (result) {
      membershipState.addIsBookmarked(false)
    }

    this.changing = false
  }

  // ------------------------------------------------------------------------------------------------
  // methods

  /**
   * ブックマークが登録されているかチェック
   *
   * @param urlPath チェックするパス
   * @returns 登録の有無。
   *     true：登録済み／false：未登録
   */
  private async checkBookmark(urlPath: string): Promise<boolean> {
    const form = { url: urlPath }
    const res = await executeApiAfterLoginCheck<{ result: boolean }>(
      PathUtil.makePath('/member/api/bookmark_check'),
      form
    )

    return res.result
  }

  /**
   * ブックマークが登録されているかチェック
   *
   * @param urlPathList チェックするパスのリスト
   * @returns パスごとの登録の有無
   */
  private async checkBookmarks(
    urlPathList: string[]
  ): Promise<Record<string, boolean>> {
    const form = { url: urlPathList }
    const res = await executeApiAfterLoginCheck<{
      result: Record<string, boolean>
    }>(PathUtil.makePath('/member/api/bookmark_check'), form)

    // falseがリターンされた場合、空で返却
    if (!res.result) {
      return {}
    }

    return res.result
  }

  /**
   * ブックマーク追加
   */
  private async addBookmark(
    urlPath: string,
    pageTitle: string
  ): Promise<boolean> {
    await membershipState.checkLogin()
    if (!membershipState.isLogin) {
      // 未ログインの場合
      // ログイン後にお気に入り追加処理を実行するためにフラグを変更する
      membershipState.addWaitingForAddBookmark(1)
      membershipState.addBookmarkDivision(1)

      // ログイン後に追加処理を実施するためにパラメータを保存
      this.isNonLoginAlertModalVisible = true
      membershipState.addLinkUrlPendingRegistration(urlPath)
      membershipState.addLinkUrlTitlePendingRegistration(pageTitle)

      return false
    }

    // 既に登録済みかチェックする
    const bookmarked = await this.checkBookmark(urlPath)
    if (bookmarked) {
      return true
    }

    // APIを実行する
    const form = {
      url: urlPath,
      title: pageTitle
    }
    const res = await executeApiAfterLoginCheck<ApiResponse>(
      PathUtil.makePath('/member/api/bookmark_add'),
      form
    )

    if (res.result === 'success') {
      // 登録に成功
      // GTM連携用のイベントを送信
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        'favorite-url': urlPath,
        event: 'MypageFavoriteEvent'
      })

      return true
    } else {
      // 登録に失敗
      this.setErrorMessage(res.errordata || {})
      return false
    }
  }

  /**
   * ブックマーク削除
   */
  private async removeBookmark(urlPath: string): Promise<boolean> {
    await membershipState.checkLogin()
    if (!membershipState.isLogin) {
      // 未ログインの場合、終了
      return false
    }

    const form = { url: urlPath }
    const res = await executeApiAfterLoginCheck<ApiResponse>(
      PathUtil.makePath('/member/api/bookmark_delete'),
      form
    )

    if (res.result === 'success') {
      // 削除に成功
      return true
    } else {
      // 削除に失敗
      this.setErrorMessage(res.errordata || {})
      return false
    }
  }

  /**
   * エラーメッセージをセットする
   */
  private setErrorMessage(message: Record<string, unknown>) {
    // エラーメッセージを表示
    this.resultError = JsonUtil.jsonToObject<ErrorFormResponse>(
      ErrorFormResponse,
      message
    )

    if (this.resultError.url !== undefined) {
      this.resultError.url.forEach((e) => alert(e))
    }
    if (this.resultError.title !== undefined) {
      this.resultError.title.forEach((e) => alert(e))
    }
    if (this.resultError.common !== undefined) {
      this.resultError.common.forEach((e) => alert(e))
    }
  }

  /**
   * 未ログインモーダルを閉じる
   */
  private closeNonLoginAlterModal() {
    this.isNonLoginAlertModalVisible = false
    this.changing = false
  }
}
